/**
 * DECISION MEMORY.
 *
 * Construction writes decisions into meeting notes and then remembers them
 * through hindsight — by which time nobody can say what was actually known at
 * the time. An episode is the opposite: the decision world frozen BEFORE the
 * outcome, kept whole.
 *
 * Every panel here is AS RECORDED. Nothing on this screen reaches for current
 * project state, because redrawing a frozen record with today's facts would
 * quietly rewrite what a person knew when they decided.
 */
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  TemporalityMarker,
  MissingInformationCallout,
  ContractMismatchCallout,
} from "./RuntimePrimitives";
import {
  EPISODE_CONTRACT,
  presentEpisodeAvailability,
} from "./episodePresentation";
import { Cpu, User, GitBranch, Gavel, Eye, Scale, History } from "lucide-react";

const DELTA_TONE = {
  neutral: "bg-muted text-muted-foreground border-border",
  informative: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  unknown: "bg-muted text-muted-foreground border-border",
};

const Section = ({ icon: Icon, title, children, note }) => (
  <div className="space-y-1 min-w-0">
    <p className="text-[11px] font-medium flex items-center gap-1 text-muted-foreground">
      <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
      {title}
    </p>
    {note && <p className="text-[10px] text-muted-foreground italic">{note}</p>}
    {children}
  </div>
);

/**
 * One human contribution.
 *
 * Labelled HUMAN ASSERTED and UNVERIFIED because that is what it is. The
 * schema has no promotion path and this slice has no verification surface, so
 * anything stronger would be a claim OMNΛS cannot make.
 */
const ContextRecord = ({ record }) => (
  <div
    className="rounded border border-violet-500/40 bg-violet-500/10 p-2 space-y-1 min-w-0"
    data-testid="human-context"
  >
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant="outline" className="text-[10px] font-normal border-violet-500/40">
        {record.provenance}
      </Badge>
      <Badge variant="outline" className="text-[10px] font-normal">
        {record.verificationState}
      </Badge>
      {record.rationaleLabel && (
        <span className="text-[10px] text-muted-foreground">{record.rationaleLabel}</span>
      )}
    </div>
    {/* The human's words, never reworded. */}
    <p className="text-[12px] break-words">{record.statement}</p>
    {record.basis && (
      <p className="text-[10px] text-muted-foreground">Basis: {record.basis}</p>
    )}
    {record.freshnessAsOf && (
      <p className="text-[10px] text-muted-foreground">
        Said to be current as of {record.freshnessAsOf}
      </p>
    )}
    {record.scope && (
      <p className="text-[10px] text-muted-foreground font-mono">{record.scope.label}</p>
    )}
    {record.supersedesId && (
      <p className="text-[10px] text-muted-foreground">
        Corrects an earlier record, which is kept.
      </p>
    )}
  </div>
);

const EpisodeCard = ({ episode }) => (
  <Card className="border-amber-500/25 bg-amber-500/[0.02]" data-testid="episode-card">
    <CardContent className="p-3 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium break-words">{episode.surfaceLabel}</p>
          <p className="text-[10px] text-muted-foreground font-mono break-all">
            {episode.decisionRef}
          </p>
        </div>
        {/* Fixed AS_RECORDED. This panel never shows live state. */}
        <TemporalityMarker temporality={episode.temporality} at={episode.machineStateV1.createdAt} />
      </div>

      {/* The derived delta — never stored, never human-graded. */}
      <div>
        <Badge
          variant="outline"
          className={`${DELTA_TONE[episode.deltaDisplay.tone] || DELTA_TONE.neutral} font-normal text-[11px]`}
          data-testid="decision-delta"
        >
          {episode.deltaDisplay.label}
        </Badge>
        <p className="text-[10px] text-muted-foreground mt-1">{episode.deltaDisplay.meaning}</p>
      </div>

      <Section icon={Cpu} title="What OMNΛS held">
        {episode.machineStateV1.present ? (
          <>
            {episode.machineStateV1.sourceFingerprint && (
              <p className="text-[10px] text-muted-foreground font-mono break-all">
                basis {episode.machineStateV1.sourceFingerprint.slice(0, 12)}
              </p>
            )}
            <pre className="text-[10px] bg-muted/30 rounded p-2 overflow-x-auto max-h-40">
              {JSON.stringify(episode.machineStateV1.payload, null, 2)}
            </pre>
          </>
        ) : (
          <p className="text-[11px] text-muted-foreground">No machine state was recorded.</p>
        )}
      </Section>

      <Section
        icon={User}
        title={`What a human added (${episode.humanContext.length})`}
        note={
          episode.humanContext.length === 0
            ? "Nobody recorded context against this decision. That is a gap in the record, not a judgement."
            : null
        }
      >
        {episode.humanContext.map((record) => (
          <ContextRecord key={record.recordId} record={record} />
        ))}
      </Section>

      <Section
        icon={GitBranch}
        title="Did OMNΛS recompute?"
        note={
          episode.machineStateV2.linked
            ? null
            : "No recomputation was explicitly linked. OMNΛS does not guess a link from timestamps."
        }
      >
        {episode.machineStateV2.linked && (
          <p className="text-[11px] font-mono break-all">
            {episode.machineStateV2.proposalIds.join(", ")}
          </p>
        )}
      </Section>

      <Section icon={Gavel} title="What was committed">
        {episode.humanCommitment.committed ? (
          <>
            <p className="text-[11px]">
              {episode.humanCommitment.state} · {episode.humanCommitment.decidedAt}
            </p>
            {episode.humanCommitment.note && (
              <p className="text-[11px] text-muted-foreground break-words">
                {episode.humanCommitment.note}
              </p>
            )}
          </>
        ) : (
          <p className="text-[11px] text-muted-foreground">Nobody has committed yet.</p>
        )}
      </Section>

      <Section icon={Eye} title="What reality showed">
        {episode.observedReality.written ? null : (
          <p className="text-[11px] text-muted-foreground">
            Nothing is recorded yet — not because the work produced nothing, but because no
            writer exists for these facts.
          </p>
        )}
        {episode.observedReality.missingWriters.map((w) => (
          <p key={w.column || w.fact} className="text-[10px] text-muted-foreground">
            {w.fact} — {w.status}
            {w.detail ? `: ${w.detail}` : ""}
          </p>
        ))}
      </Section>

      <Section
        icon={Scale}
        title="Calibration"
        note={
          episode.calibration.calibratesClaimsNotPeople
            ? "OMNΛS calibrates the claims a decision made. It does not rate the people who made it."
            : null
        }
      >
        {episode.calibration.claims.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">No claims are recorded.</p>
        ) : (
          episode.calibration.claims.map((claim) => (
            <p key={claim.claim} className="text-[11px] break-words">
              <span className="text-muted-foreground">{claim.claim}: </span>
              {String(claim.value)} — {claim.display.label}
              {claim.display.meaning && (
                <span className="block text-[10px] text-muted-foreground">
                  {claim.display.meaning}
                </span>
              )}
            </p>
          ))
        )}
      </Section>

      {episode.hasReplayContext && (
        <Section
          icon={History}
          title="What the human was looking at"
          note="Frozen at the decision moment. A simulation basis keeps its own HYPOTHETICAL label."
        >
          <pre className="text-[10px] bg-muted/30 rounded p-2 overflow-x-auto max-h-40">
            {JSON.stringify(episode.replayContext, null, 2)}
          </pre>
        </Section>
      )}
    </CardContent>
  </Card>
);

const DecisionMemoryPanel = ({ memory, loading, error, contractVersion }) => {
  if (loading) {
    return (
      <Card className="border-border/60">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">Reading what was known at the time…</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    const availability = presentEpisodeAvailability(error);
    return (
      <Card className="border-border/60" data-testid="memory-unavailable">
        <CardContent className="p-4 space-y-1.5">
          <p className="text-sm font-medium">{availability.title}</p>
          <p className="text-[12px] text-muted-foreground">{availability.detail}</p>
        </CardContent>
      </Card>
    );
  }

  if (!memory || !memory.hasEpisodes) {
    return (
      <Card className="border-border/60" data-testid="decision-memory">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            No award decision has been recorded on this project yet. An episode is written when a
            decision is made, not at closeout.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="decision-memory">
      <ContractMismatchCallout received={contractVersion} supported={EPISODE_CONTRACT} />
      <p className="text-[12px] text-muted-foreground">
        Award decisions across this project, as they were recorded. OMNΛS offers no
        package filter here, so this list is project-wide rather than scoped to the package
        you are looking at.
      </p>
      {memory.episodes.map((episode) => (
        <EpisodeCard key={`${episode.surface}-${episode.decisionRef}`} episode={episode} />
      ))}
      <MissingInformationCallout
        items={[
          "Observed reality and calibration stay unwritten until the execution observation writers land. Nothing here is inferred to fill that gap.",
        ]}
        title="What this layer cannot yet show"
      />
    </div>
  );
};

export default DecisionMemoryPanel;
